import {
  ErpConnectionApiClient,
  ConnectToErpResponse,
  FetchLatestDataResponse,
  AvailableCompaniesResponse,
  CompleteConnectionResponse,
  ExtensionDownloadResponse
} from "@/clients/erpConnectionApiClient";

/**
 * ERP Service
 * Handles business logic for ERP connections
 */
export class ErpService {
  /**
   * Initiate connection to ERP system
   * @returns Promise with authorization URL
   */
  async connectToErp(): Promise<ConnectToErpResponse> {
    const response = await ErpConnectionApiClient.connectToErp();
    console.log(`ERP connection initiated: ${response.provider}`);
    return response;
  }

  /**
   * Fetch latest data from connected ERP system
   * Company ID is discovered automatically
   * @param code - OAuth authorization code
   * @returns Promise with fetch result including discovered company
   */
  async fetchLatestData(code: string): Promise<FetchLatestDataResponse> {
    const response = await ErpConnectionApiClient.fetchLatestData(code);
    console.log(`ERP data fetch result:`, response);
    return response;
  }

  /**
   * Get available companies from ERP (Step 1 of new flow)
   * @param code - OAuth authorization code
   * @returns Promise with session ID and list of companies
   */
  async getAvailableCompanies(code: string): Promise<AvailableCompaniesResponse> {
    const response = await ErpConnectionApiClient.getAvailableCompanies(code);
    console.log(`Found ${response.companies.length} companies for ERP connection`);
    return response;
  }

  /**
   * Complete ERP connection with selected company (Step 2 of new flow)
   * @param sessionId - Session ID from getAvailableCompanies
   * @param companyId - Selected Business Central company ID
   * @returns Promise with connection result
   */
  async completeConnection(sessionId: string, companyId: string): Promise<CompleteConnectionResponse> {
    const response = await ErpConnectionApiClient.completeConnection(sessionId, companyId);
    console.log(`ERP connection completed:`, response);
    return response;
  }

  /**
   * Sync ERP data in the background using existing connection
   * Uses throttling (20 min) and incremental fetching
   * Silent operation - no user notifications
   * @returns Promise with fetch result or null if connection doesn't exist or sync skipped
   */
  async syncDataBackground(): Promise<FetchLatestDataResponse | null> {
    const response = await ErpConnectionApiClient.syncDataBackground();
    if (response) {
      console.log(`Background ERP sync completed:`, response);
    }
    return response;
  }

  /**
   * Get extension download information
   * Returns metadata about the Business Central extension to install
   * Future: may be personalized per company/customer
   * @returns Promise with extension download URL and metadata
   */
  async getExtensionDownload(): Promise<ExtensionDownloadResponse> {
    const response = await ErpConnectionApiClient.getExtensionDownload();
    console.log(`Extension download info:`, response);
    return response;
  }
}

export default ErpService;
